import { KEY_MOMENTS_PROMPT } from '../config/prompts';
import { getConfigValue, getPrompt } from '../config/pipeline-registry';
import { hashCanonicalJson, sha256Text } from '../quote-engine/canonical-json';
import { governedSupportRouteForSignalV1 } from '../quote-engine/governed-model-route-context';
import { callOpenRouter, callOpenRouterVision, getMaxOutputTokens } from './ai-api';
import {
    beginEvalAiLogicalStage,
    withEvalAiPaidRequest,
} from './eval-ai-single-attempt-policy';
import { pickModelForTask } from './model-selector';
import { classifyAiServiceFailure } from './ai-service-failure';
import { SecurityUtils } from './security';
import { getStage1VideoProcessingMode } from './stage1-video-policy';
import { withTimeout, yieldToMain } from '../utils/helpers';

export type Stage1VideoEvidenceMode = 'inline' | 'poster-only' | 'reference-only';

export interface Stage1VideoEvidenceFrame {
    data: string;
    timestamp: number;
    label: string;
    context: string;
    name: string;
}

export interface Stage1VideoEvidenceTranscript {
    text: string;
    segments: Array<Record<string, unknown>>;
}

export interface Stage1VideoEvidenceReceipt {
    schemaVersion: 1;
    source: {
        name: string;
        bytes: number;
        contentType: string;
        lastModified: number | null;
    };
    policyMode: string;
    executionMode: Stage1VideoEvidenceMode;
    durationSeconds: number | null;
    transcriptChars: number;
    transcriptHash: string | null;
    frameCount: number;
    frames: Array<{
        name: string;
        timestamp: number;
        label: string;
        context: string;
        dataHash: string;
    }>;
    coverageStatus: 'full' | 'poster-only' | 'reference-only';
    persistence: {
        localArtifacts: false;
        cloudProxy: false;
        sourceIncluded: false;
    };
    receiptHash: string;
}

export interface Stage1VideoEvidence {
    transcript: Stage1VideoEvidenceTranscript | null;
    frames: Stage1VideoEvidenceFrame[];
    duration: number | null;
    receipt: Stage1VideoEvidenceReceipt;
}

export interface Stage1VideoEvidenceOptions {
    /** Canonical project owner when used by the production preparation flow. */
    projectId?: string | null;
    apiKey?: string;
    model?: string;
    audioModel?: string;
    signal?: AbortSignal;
    /** Opaque evaluator fan-out id; hashed before entering the AI journal. */
    logicalCallId?: string;
    onProgress?: (progress: number, message: string) => void;
    /** The evaluator is deliberately no-persist. Values other than false fail. */
    persistLocalArtifacts?: false;
    /** The evaluator is deliberately no-persist. Values other than false fail. */
    persistCloudProxy?: false;
    /** Raw source bytes are never placed in a receipt. Values other than false fail. */
    includeSourceInReceipt?: false;
    /** Evaluation uses deterministic evenly-spaced frames to avoid a second AI call. */
    deterministicMoments?: boolean;
    /** Tests may provide browser-free implementations for the expensive seams. */
    dependencies?: Partial<Stage1VideoEvidenceDependencies>;
}

interface Stage1VideoEvidenceDependencies {
    createObjectUrl(file: File): string;
    revokeObjectUrl(url: string): void;
    readDuration(url: string, signal?: AbortSignal): Promise<number>;
    extractAudio(file: File, signal?: AbortSignal): Promise<Blob>;
    transcribe(
        audio: Blob,
        fileName: string,
        options: Stage1VideoEvidenceOptions,
    ): Promise<Stage1VideoEvidenceTranscript>;
    chooseMoments(
        transcript: Stage1VideoEvidenceTranscript,
        duration: number,
        options: Stage1VideoEvidenceOptions,
    ): Promise<Array<{ time: number; label: string; context: string }>>;
    extractFrames(
        url: string,
        moments: Array<{ time: number; label: string; context: string }>,
        fileName: string,
        signal?: AbortSignal,
    ): Promise<Stage1VideoEvidenceFrame[]>;
    capturePoster(
        url: string,
        fileName: string,
        duration: number,
        signal?: AbortSignal,
    ): Promise<Stage1VideoEvidenceFrame | null>;
}

function abortError(message = 'Video evidence extraction cancelled'): Error {
    const error = new Error(message);
    error.name = 'AbortError';
    return error;
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw abortError();
}

export function formatVideoTimestamp(seconds: number): string {
    const safe = Math.max(0, Number(seconds) || 0);
    const mins = Math.floor(safe / 60);
    const secs = Math.floor(safe % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function getVideoEvidenceMaxFrames(durationSeconds: number): number {
    if (durationSeconds < 10) return 1;
    if (durationSeconds < 30) return 2;
    if (durationSeconds < 120) return 4;
    if (durationSeconds < 300) return 8;
    return 15;
}

export function buildVideoFallbackMoments(videoDuration: number) {
    const safeDuration = Math.max(0, Number(videoDuration) || 0);
    const count = getVideoEvidenceMaxFrames(safeDuration);
    if (count === 1) {
        const time = Math.max(0, Math.min(safeDuration * 0.5, Math.max(0, safeDuration - 0.1), 5));
        return [{ time, label: 'Video frame', context: '' }];
    }
    const step = safeDuration / (count + 1);
    return Array.from({ length: count }, (_, index) => {
        const time = Math.max(0, Math.min(step * (index + 1), Math.max(0, safeDuration - 0.5)));
        return { time, label: `Frame at ${formatVideoTimestamp(time)}`, context: '' };
    });
}

async function audioBufferToWav(buffer: AudioBuffer, signal?: AbortSignal): Promise<Blob> {
    const channels = buffer.numberOfChannels;
    const length = buffer.length * channels * 2 + 44;
    const output = new ArrayBuffer(length);
    const view = new DataView(output);
    let position = 0;
    let offset = 0;
    const write16 = (value: number) => { view.setUint16(position, value, true); position += 2; };
    const write32 = (value: number) => { view.setUint32(position, value, true); position += 4; };
    write32(0x46464952);
    write32(length - 8);
    write32(0x45564157);
    write32(0x20746d66);
    write32(16);
    write16(1);
    write16(channels);
    write32(buffer.sampleRate);
    write32(buffer.sampleRate * 2 * channels);
    write16(channels * 2);
    write16(16);
    write32(0x61746164);
    write32(length - position - 4);
    const channelData = Array.from({ length: channels }, (_, index) => buffer.getChannelData(index));
    position = 0;
    while (position < buffer.length) {
        throwIfAborted(signal);
        for (let channel = 0; channel < channels; channel += 1) {
            const sample = Math.max(-1, Math.min(1, channelData[channel]?.[position] || 0));
            view.setInt16(44 + offset, sample < 0 ? sample * 32768 : sample * 32767, true);
            offset += 2;
        }
        position += 1;
        if (position % 100_000 === 0) await yieldToMain();
    }
    return new Blob([output], { type: 'audio/wav' });
}

export interface DecodeAudioToPcmWavOptions {
    signal?: AbortSignal;
    /** Keep only the first this-many seconds; null keeps the whole recording. */
    maxDurationSeconds?: number | null;
    decodeTimeoutMs?: number;
}

/**
 * Decode any audio the browser can play (a video's soundtrack, an MP3, an M4A
 * voice memo) into the 16 kHz mono 16-bit PCM WAV that
 * splitPcmWavForTranscription can cut into pieces the provider accepts.
 */
export async function decodeAudioToPcmWav(
    file: Blob,
    { signal, maxDurationSeconds = null, decodeTimeoutMs = 30_000 }: DecodeAudioToPcmWavOptions = {},
): Promise<Blob> {
    throwIfAborted(signal);
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) throw new Error('Web Audio API is unavailable');
    let context: AudioContext | null = null;
    try {
        let bytes: ArrayBuffer | null = await file.arrayBuffer();
        throwIfAborted(signal);
        context = new AudioContextCtor();
        let decoded = await withTimeout(context.decodeAudioData(bytes), decodeTimeoutMs, 'Audio decoding');
        bytes = null;
        throwIfAborted(signal);
        // 16 kHz is the rate speech models are trained on. 8 kHz is telephone
        // quality and costs words on a windy site walkthrough. The audio is
        // sent in pieces (splitPcmWavForTranscription), so the doubled size
        // does not reach the provider's per-request limit.
        const targetRate = 16_000;
        const duration = maxDurationSeconds == null ? decoded.duration : Math.min(decoded.duration, maxDurationSeconds);
        const targetLength = Math.ceil(duration * targetRate);
        let downsampled: AudioBuffer;
        try {
            const offline = new OfflineAudioContext(1, targetLength, targetRate);
            const source = offline.createBufferSource();
            source.buffer = decoded;
            source.connect(offline.destination);
            source.start(0);
            if (decoded.duration > duration) source.stop(duration);
            downsampled = await offline.startRendering();
            source.buffer = null;
            source.disconnect();
        } catch {
            downsampled = decoded;
        }
        decoded = null as unknown as AudioBuffer;
        await context.close().catch(() => undefined);
        context = null;
        return await audioBufferToWav(downsampled, signal);
    } finally {
        if (context) await context.close().catch(() => undefined);
    }
}

async function extractAudioFromVideo(file: File, signal?: AbortSignal): Promise<Blob> {
    throwIfAborted(signal);
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) throw new Error('Web Audio API is unavailable');
    try {
        return await decodeAudioToPcmWav(file, { signal, maxDurationSeconds: 600 });
    } catch (cause: any) {
        if (cause?.name === 'AbortError') throw cause;
        const message = String(cause?.message || cause || 'unknown decoder error');
        throw new Error(`Could not extract video audio: ${message}`, { cause });
    }
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error || new Error('Could not read audio blob'));
        reader.onloadend = () => resolve(String(reader.result || '').split(',')[1] || '');
        reader.readAsDataURL(blob);
    });
}

// OpenRouter serves Voxtral from Mistral's shared provider pool, which answers
// 429 `upstream_provider_shared_pool` to any request carrying more than about
// 1 MB of audio, on every key (measured 2026-10-20: 960 KB of WAV passed six
// times running, 1.28 MB failed every time, at 8 kHz and at 16 kHz). A whole
// site video in one WAV was therefore refused every time, and the operator was
// told to add an OpenRouter key, which cannot help. Pieces stay well under it.
export const TRANSCRIPTION_PIECE_MAX_PCM_BYTES = 768_000; // 24 s of 16 kHz mono 16-bit

const TRANSCRIPTION_INSTRUCTION = 'Transcribe this audio file verbatim. Identify different speakers if possible. Output ONLY the transcript text.';
// A piece that hits a busy provider is retried here, so a 10-minute walkthrough
// is not thrown away (and paid for again) because piece 17 met a rate limit.
const TRANSCRIPTION_PIECE_RETRY_WAITS_MS = [5_000, 15_000];

export interface TranscriptionPiece {
    blob: Blob;
    startSeconds: number;
    endSeconds: number;
}

async function readBlobBytes(blob: Blob): Promise<ArrayBuffer> {
    if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error || new Error('Could not read audio blob'));
        reader.onloadend = () => resolve(reader.result as ArrayBuffer);
        reader.readAsArrayBuffer(blob);
    });
}

function pcmWavHeader(dataBytes: number, sampleRate: number, channels: number): ArrayBuffer {
    const header = new ArrayBuffer(44);
    const view = new DataView(header);
    const blockAlign = channels * 2;
    [0x46464952, 36 + dataBytes, 0x45564157, 0x20746d66, 16].forEach((value, index) => view.setUint32(index * 4, value, true));
    view.setUint16(20, 1, true);
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    view.setUint32(36, 0x61746164, true);
    view.setUint32(40, dataBytes, true);
    return header;
}

/**
 * Split the 16-bit PCM WAV that extractAudioFromVideo writes into consecutive
 * pieces of at most `maxPcmBytes` of samples, each a valid WAV with its own
 * header. Pieces are equal in length, so no short tail is sent on its own
 * (a model given a second of noise tends to invent a sentence). Anything that
 * is not that exact WAV layout, or is already small enough, comes back whole.
 */
export async function splitPcmWavForTranscription(
    audio: Blob,
    maxPcmBytes: number = TRANSCRIPTION_PIECE_MAX_PCM_BYTES,
): Promise<TranscriptionPiece[]> {
    const whole = [{ blob: audio, startSeconds: 0, endSeconds: 0 }];
    if (audio.size <= 44 + maxPcmBytes) return whole;
    let view: DataView;
    try {
        view = new DataView(await readBlobBytes(audio.slice(0, 44)));
    } catch {
        return whole;
    }
    if (view.byteLength < 44
        || view.getUint32(0, true) !== 0x46464952 || view.getUint32(8, true) !== 0x45564157
        || view.getUint32(12, true) !== 0x20746d66 || view.getUint16(20, true) !== 1
        || view.getUint16(34, true) !== 16 || view.getUint32(36, true) !== 0x61746164) {
        return whole;
    }
    const channels = view.getUint16(22, true);
    const sampleRate = view.getUint32(24, true);
    const blockAlign = channels * 2;
    const dataBytes = Math.min(view.getUint32(40, true), audio.size - 44);
    if (!channels || !sampleRate || dataBytes <= maxPcmBytes) return whole;
    const totalFrames = Math.floor(dataBytes / blockAlign);
    const pieceCount = Math.ceil(dataBytes / maxPcmBytes);
    const framesPerPiece = Math.ceil(totalFrames / pieceCount);
    const pieces: TranscriptionPiece[] = [];
    for (let startFrame = 0; startFrame < totalFrames; startFrame += framesPerPiece) {
        const endFrame = Math.min(totalFrames, startFrame + framesPerPiece);
        const bytes = (endFrame - startFrame) * blockAlign;
        pieces.push({
            blob: new Blob([
                pcmWavHeader(bytes, sampleRate, channels),
                audio.slice(44 + startFrame * blockAlign, 44 + endFrame * blockAlign),
            ], { type: 'audio/wav' }),
            startSeconds: startFrame / sampleRate,
            endSeconds: endFrame / sampleRate,
        });
    }
    return pieces;
}

/**
 * Wait (abortable, no deadline) until the browser's shared AI rate limiter has
 * a free slot. It allows 30 calls a minute per tab and refuses the 31st with a
 * plain error that is not retried, so a long clip's pieces, next to photo and
 * PDF calls, would set the clip aside. Same wait as the Assistant's
 * waitForApiRateSlot (assistant-agent.js).
 */
export async function waitForTranscriptionRateSlot(signal?: AbortSignal | null): Promise<void> {
    while (SecurityUtils.rateLimiter.getRemainingRequests('api') <= 0) {
        if (signal?.aborted) throw abortError();
        await abortableWait(1_500, signal || undefined);
    }
}

function abortableWait(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) { reject(abortError()); return; }
        const onAbort = () => { clearTimeout(timer); reject(abortError()); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

export interface TranscribeAudioPiecesOptions {
    apiKey: string;
    model: string;
    /** The registry task the calls are made and logged under. */
    task: string;
    signal?: AbortSignal;
    projectId?: string | null;
    logicalCallId?: string;
    /** A governed (evaluation) route gets exactly one attempt per piece. */
    governed?: boolean;
    shouldStop?: () => boolean;
    onProgress?: (progress: number, message: string) => void;
}

/**
 * Transcribe audio through the gateway in pieces the provider accepts
 * (splitPcmWavForTranscription), waiting for the shared rate limiter between
 * pieces and retrying a piece the provider was too busy for. A video's
 * soundtrack and an imported recording both come through here.
 */
export async function transcribeAudioInPieces(
    audio: Blob,
    options: TranscribeAudioPiecesOptions,
): Promise<Stage1VideoEvidenceTranscript> {
    const { apiKey, model, task, signal } = options;
    const pieces = await splitPcmWavForTranscription(audio);
    throwIfAborted(signal);
    const segments: Array<Record<string, unknown>> = [];
    for (const [index, piece] of pieces.entries()) {
        if (options.shouldStop?.()) throw abortError('Transcription cancelled');
        const base64 = await blobToBase64(piece.blob);
        throwIfAborted(signal);
        const mime = piece.blob.type.startsWith('audio/') ? piece.blob.type : 'audio/wav';
        const callId = options.logicalCallId
            ? `${options.logicalCallId}:transcription${pieces.length > 1 ? `:${index + 1}` : ''}`
            : undefined;
        let text: unknown;
        for (let attempt = 0; ; attempt += 1) {
            try {
                if (pieces.length > 1) await waitForTranscriptionRateSlot(signal);
                text = await callOpenRouterVision({
                    apiKey,
                    model,
                    prompt: TRANSCRIPTION_INSTRUCTION,
                    images: [`data:${mime};base64,${base64}`],
                    pipelineId: task,
                    task,
                    stageName: '1',
                    timeoutMs: 0,
                    signal,
                    responseFormat: undefined,
                    reasoningEffort: undefined,
                    projectId: options.projectId ?? null,
                    logicalCallId: callId,
                    supportOperation: 'audio_transcription',
                });
                break;
            } catch (error) {
                const wait = TRANSCRIPTION_PIECE_RETRY_WAITS_MS[attempt];
                if (wait === undefined || options.governed || options.logicalCallId || signal?.aborted
                    || !classifyAiServiceFailure(error).transient) throw error;
                options.onProgress?.(35, `Transcription busy, retrying part ${index + 1} of ${pieces.length}`);
                await abortableWait(wait, signal);
            }
        }
        const pieceText = String(text || '').trim();
        if (pieceText && pieces.length > 1) {
            segments.push({ start: piece.startSeconds, end: piece.endSeconds, text: pieceText });
        }
        if (pieces.length > 1) {
            options.onProgress?.(35 + Math.round(30 * (index + 1) / pieces.length), `Transcribed part ${index + 1} of ${pieces.length}`);
        }
        if (pieces.length === 1) return { text: pieceText, segments: [] };
    }
    return { text: segments.map(segment => segment.text).join('\n'), segments };
}

async function transcribeAudio(
    audio: Blob,
    fileName: string,
    options: Stage1VideoEvidenceOptions,
): Promise<Stage1VideoEvidenceTranscript> {
    const { apiKey, model, audioModel, signal } = options;
    throwIfAborted(signal);
    if (!apiKey) throw new Error('API key is required for video transcription');
    if (audio.size > 20 * 1024 * 1024) throw new Error('Audio exceeds the 20 MB transcription limit');
    const task = 'video-processing';
    const governedSupportRoute = governedSupportRouteForSignalV1(signal, 'audio_transcription');
    if (governedSupportRoute || apiKey.startsWith('sk-or-') || apiKey === 'gateway') {
        const selectedModel = governedSupportRoute?.model || pickModelForTask(task, audioModel || model);
        if (!selectedModel) throw new Error('Video transcription model is not configured');
        return transcribeAudioInPieces(audio, {
            apiKey,
            model: selectedModel,
            task,
            signal,
            projectId: options.projectId ?? null,
            ...(options.logicalCallId ? { logicalCallId: options.logicalCallId } : {}),
            governed: !!governedSupportRoute,
            ...(options.onProgress ? { onProgress: options.onProgress } : {}),
        });
    }
    const transcriptionModel = 'whisper-1';
    const payload = new FormData();
    const safeName = /\.(mp3|mp4|mpeg|mpga|m4a|wav|webm|flac|ogg|oga)$/i.test(fileName)
        ? fileName
        : fileName.replace(/\.[^.]+$/, '.wav');
    payload.append('file', audio, safeName);
    payload.append('model', transcriptionModel);
    payload.append('response_format', 'verbose_json');
    payload.append('timestamp_granularities[]', 'segment');
    const evalAttempt = beginEvalAiLogicalStage({
        pipelineId: task,
        stageName: '1',
        task,
        model: transcriptionModel,
        logicalCallId: options.logicalCallId ? `${options.logicalCallId}:transcription` : undefined,
    });
    const response = await withEvalAiPaidRequest(evalAttempt, {
        route: 'browser-direct-openai-transcription',
        task,
        model: transcriptionModel,
        // FormData cannot be serialized portably. Hash a stable, content-free
        // request envelope instead; no audio bytes or provider key enter the
        // evaluator journal.
        requestPayload: {
            endpoint: '/v1/audio/transcriptions',
            fileName: safeName,
            audioBytes: audio.size,
            contentType: audio.type || 'application/octet-stream',
            model: transcriptionModel,
            responseFormat: 'verbose_json',
            timestampGranularity: 'segment',
        },
    }, () => fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiKey}` },
        body: payload,
        signal,
    }));
    if (!response.ok) throw new Error(`Transcription failed: ${response.status}`);
    const result = await response.json();
    return {
        text: String(result?.text || '').trim(),
        segments: Array.isArray(result?.segments) ? result.segments : [],
    };
}

async function readVideoDuration(url: string, signal?: AbortSignal): Promise<number> {
    throwIfAborted(signal);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    try {
        await withTimeout(new Promise<void>((resolve, reject) => {
            const onAbort = () => reject(abortError());
            signal?.addEventListener('abort', onAbort, { once: true });
            video.onloadedmetadata = () => {
                signal?.removeEventListener('abort', onAbort);
                resolve();
            };
            video.onerror = () => {
                signal?.removeEventListener('abort', onAbort);
                reject(new Error('Cannot load video metadata'));
            };
        }), 15_000, 'Video metadata load');
        const duration = Number(video.duration);
        return Number.isFinite(duration) && duration >= 0 ? duration : 0;
    } finally {
        video.src = '';
        video.load();
    }
}

async function chooseKeyMoments(
    transcript: Stage1VideoEvidenceTranscript,
    duration: number,
    options: Stage1VideoEvidenceOptions,
) {
    const fallback = buildVideoFallbackMoments(duration);
    if (!options.apiKey) return fallback;
    const maxFrames = getVideoEvidenceMaxFrames(duration);
    const segmentText = transcript.segments.length > 0
        ? transcript.segments.map((segment: any) => `[${formatVideoTimestamp(Number(segment.start) || 0)}] ${segment.text || ''}`).join('\n')
        : `[00:00] ${transcript.text}`;
    const prompt = (getPrompt('key-moments') || KEY_MOMENTS_PROMPT)
        .replace('${videoDuration}', String(Math.round(duration)))
        .replace('${maxFrames}', String(maxFrames))
        .replace('${transcript}', segmentText)
        + (duration < 30 ? ' This is a very short video - 1-2 frames is sufficient.' : '');
    try {
        const keyMomentsModel = pickModelForTask('key-moments', options.model);
        const response = await callOpenRouter({
            apiKey: options.apiKey,
            model: keyMomentsModel,
            messages: [{ role: 'user', content: prompt }],
            temperature: getConfigValue('video-processing', 'temperature') ?? 0.3,
            maxTokens: getMaxOutputTokens(keyMomentsModel),
            pipelineId: 'key-moments',
            task: 'key-moments',
            stageName: '1',
            signal: options.signal,
            logicalCallId: options.logicalCallId ? `${options.logicalCallId}:moments` : undefined,
            responseFormat: undefined,
            timeoutMs: undefined,
            projectId: options.projectId ?? null,
            originator: null,
            modelRouteAttempt: null,
        });
        const match = String(response || '').match(/\[[\s\S]*\]/);
        if (!match) return fallback;
        let moments = JSON.parse(match[0]);
        moments = (Array.isArray(moments) ? moments : [])
            .map((moment) => ({
                time: Number(moment?.time),
                label: String(moment?.label || `Frame at ${formatVideoTimestamp(Number(moment?.time) || 0)}`),
                context: String(moment?.context || ''),
            }))
            .filter((moment) => Number.isFinite(moment.time) && moment.time >= 0 && moment.time <= Math.max(duration, 0.5));
        if (moments.length > maxFrames) {
            const step = moments.length / maxFrames;
            moments = Array.from({ length: maxFrames }, (_, index) => moments[Math.min(moments.length - 1, Math.round(index * step))]);
        }
        return moments.length > 0 ? moments : fallback;
    } catch (error: any) {
        if (error?.name === 'AbortError') throw error;
        return fallback;
    }
}

async function extractFrames(
    url: string,
    moments: Array<{ time: number; label: string; context: string }>,
    fileName: string,
    signal?: AbortSignal,
): Promise<Stage1VideoEvidenceFrame[]> {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable');
    video.src = url;
    video.muted = true;
    const output: Stage1VideoEvidenceFrame[] = [];
    try {
        await withTimeout(new Promise<void>((resolve) => {
            video.onloadedmetadata = () => resolve();
            video.onerror = () => resolve();
        }), 15_000, 'Video metadata load');
        if (!video.videoWidth) return output;
        const maxPixels = 1280;
        let width = video.videoWidth;
        let height = video.videoHeight;
        if (width > maxPixels || height > maxPixels) {
            const scale = maxPixels / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        for (const moment of moments) {
            throwIfAborted(signal);
            try {
                await withTimeout(new Promise<void>((resolve) => {
                    video.onseeked = () => resolve();
                    video.onerror = () => resolve();
                    video.currentTime = moment.time;
                }), 10_000, `Video seek to ${moment.time}s`);
                await yieldToMain();
                context.drawImage(video, 0, 0, width, height);
                output.push({
                    data: canvas.toDataURL('image/jpeg', 0.75),
                    timestamp: moment.time,
                    label: moment.label,
                    context: moment.context,
                    name: `${fileName.replace(/\.[^.]+$/, '')}_${formatVideoTimestamp(moment.time).replace(/:/g, '-')}.jpg`,
                });
            } catch (error: any) {
                if (error?.name === 'AbortError') throw error;
            }
            await yieldToMain();
        }
        return output;
    } finally {
        video.src = '';
        video.load();
        canvas.width = 0;
        canvas.height = 0;
    }
}

async function capturePoster(
    url: string,
    fileName: string,
    duration: number,
    signal?: AbortSignal,
): Promise<Stage1VideoEvidenceFrame | null> {
    const time = Math.max(0, Math.min(duration * 0.25, Math.max(0, duration - 0.1), 5));
    const frames = await extractFrames(url, [{ time, label: 'Video poster frame', context: 'Poster-only evidence selected by the video processing policy.' }], fileName, signal);
    return frames[0] || null;
}

const DEFAULT_DEPENDENCIES: Stage1VideoEvidenceDependencies = {
    createObjectUrl: (file) => URL.createObjectURL(file),
    revokeObjectUrl: (url) => URL.revokeObjectURL(url),
    readDuration: readVideoDuration,
    extractAudio: extractAudioFromVideo,
    transcribe: transcribeAudio,
    chooseMoments: chooseKeyMoments,
    extractFrames,
    capturePoster,
};

function executionModeForPolicy(policy: ReturnType<typeof getStage1VideoProcessingMode>): Stage1VideoEvidenceMode {
    if (policy.processInline) return 'inline';
    if (policy.thumbnailOnly || policy.kind === 'worker_route') return 'poster-only';
    return 'reference-only';
}

function buildReceipt(
    file: File,
    policyMode: string,
    mode: Stage1VideoEvidenceMode,
    duration: number | null,
    transcript: Stage1VideoEvidenceTranscript | null,
    frames: Stage1VideoEvidenceFrame[],
): Stage1VideoEvidenceReceipt {
    const frameReceipts = frames.map((frame) => ({
        name: frame.name,
        timestamp: frame.timestamp,
        label: frame.label,
        context: frame.context,
        dataHash: sha256Text(frame.data),
    }));
    const base = {
        schemaVersion: 1 as const,
        source: {
            name: file.name,
            bytes: file.size,
            contentType: file.type || 'application/octet-stream',
            lastModified: Number.isFinite(file.lastModified) ? file.lastModified : null,
        },
        policyMode,
        executionMode: mode,
        durationSeconds: duration,
        transcriptChars: transcript?.text.length || 0,
        transcriptHash: transcript?.text ? sha256Text(transcript.text) : null,
        frameCount: frameReceipts.length,
        frames: frameReceipts,
        coverageStatus: (mode === 'inline' ? 'full' : mode === 'poster-only' ? 'poster-only' : 'reference-only') as Stage1VideoEvidenceReceipt['coverageStatus'],
        persistence: {
            localArtifacts: false as const,
            cloudProxy: false as const,
            sourceIncluded: false as const,
        },
    };
    return { ...base, receiptHash: hashCanonicalJson(base) };
}

/**
 * Extract the same browser evidence used by Stage 1 without persisting a raw
 * video, proxy, frame, transcript, Project or cloud object. The returned frame
 * data is ephemeral and callers must release it after the downstream stage.
 */
export async function extractStage1VideoEvidence(
    file: File,
    options: Stage1VideoEvidenceOptions = {},
): Promise<Stage1VideoEvidence> {
    if (!(file instanceof File)) throw new TypeError('A browser File is required');
    if (options.persistLocalArtifacts !== undefined && options.persistLocalArtifacts !== false) {
        throw new TypeError('Video evidence extraction cannot persist local artifacts');
    }
    if (options.persistCloudProxy !== undefined && options.persistCloudProxy !== false) {
        throw new TypeError('Video evidence extraction cannot persist a cloud proxy');
    }
    if (options.includeSourceInReceipt !== undefined && options.includeSourceInReceipt !== false) {
        throw new TypeError('Video evidence receipts cannot include source bytes');
    }
    throwIfAborted(options.signal);
    const dependencies = { ...DEFAULT_DEPENDENCIES, ...(options.dependencies || {}) };
    const policy = getStage1VideoProcessingMode(file);
    if (!policy.accepted) throw new Error(policy.statusMessage || 'Video rejected by Stage 1 policy');
    const mode = executionModeForPolicy(policy);
    const progress = (value: number, message: string) => options.onProgress?.(value, message);
    if (mode === 'reference-only') {
        progress(100, 'Reference-only video recorded');
        return {
            transcript: null,
            frames: [],
            duration: null,
            receipt: buildReceipt(file, policy.kind, mode, null, null, []),
        };
    }
    const url = dependencies.createObjectUrl(file);
    let duration: number | null = null;
    let transcript: Stage1VideoEvidenceTranscript | null = null;
    let frames: Stage1VideoEvidenceFrame[] = [];
    try {
        progress(5, 'Reading video metadata');
        duration = await dependencies.readDuration(url, options.signal);
        throwIfAborted(options.signal);
        if (mode === 'poster-only') {
            progress(70, 'Capturing poster-frame evidence');
            const poster = await dependencies.capturePoster(url, file.name, duration, options.signal);
            if (poster) frames = [poster];
            progress(100, 'Poster-frame evidence complete');
            return { transcript, frames, duration, receipt: buildReceipt(file, policy.kind, mode, duration, transcript, frames) };
        }
        progress(15, 'Extracting video audio');
        const audio = await dependencies.extractAudio(file, options.signal);
        throwIfAborted(options.signal);
        progress(35, 'Transcribing video');
        transcript = await dependencies.transcribe(audio, file.name, options);
        throwIfAborted(options.signal);
        progress(70, 'Selecting video evidence moments');
        const moments = options.deterministicMoments
            ? buildVideoFallbackMoments(duration)
            : await dependencies.chooseMoments(transcript, duration, options);
        throwIfAborted(options.signal);
        progress(85, `Extracting ${moments.length} video frame${moments.length === 1 ? '' : 's'}`);
        frames = await dependencies.extractFrames(url, moments, file.name, options.signal);
        if (frames.length === 0) {
            const poster = await dependencies.capturePoster(url, file.name, duration, options.signal);
            if (poster) frames = [poster];
        }
        progress(100, 'Video evidence complete');
        return { transcript, frames, duration, receipt: buildReceipt(file, policy.kind, mode, duration, transcript, frames) };
    } finally {
        dependencies.revokeObjectUrl(url);
    }
}
